import clsx from 'clsx';
import DefaultButton from '@/components/common/default/DefaultButton';
import Image from 'next/image';
import useBackendUser from '@/hooks/useBackendUser';
import UserMenuDialogMenu from './UserMenuDialogMenu';
import { Fragment, useEffect, useState } from 'react';
import { ImagesHeaderCloseSvg } from '@/assets/images_header.assets';
import { UserMenuDialogProps } from '@/types/props/components/layout';
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { HeaderEnum } from '@/types/translations/HeaderEnum';

export default function UserMenuDialog({
  open,
  onClose,
  ctaCountQuery,
  notificationsQuery,
}: UserMenuDialogProps) {
  const { translate } = useTypedTranslation<HeaderEnum>('header');

  const [username, setUsername] = useState<string | null>(null);

  const { data: user, isLoading: isLoadingUser } = useBackendUser();
  const { data: ctaData = null } = ctaCountQuery;
  const { data: notificationsDataQuery = null } = notificationsQuery;

  const notificationsData = notificationsDataQuery || [];

  const { buyerNotificationsCallToAc, sellerNotificationsCallToAc } =
    ctaData || {
      buyerNotificationsCallToAc: 0,
      sellerNotificationsCallToAc: 0,
    };

  useEffect(() => {
    if (user) {
      const firstLetterFirstName = String(user.firstName)
        .charAt(0)
        .toUpperCase();
      const firstLetterLastName = String(user.lastName).charAt(0).toUpperCase();

      const finalFirstName = String(user.firstName).replace(
        String(user.firstName).charAt(0),
        firstLetterFirstName
      );
      const finalLastName = String(user.lastName).replace(
        String(user.lastName).charAt(0),
        firstLetterLastName
      );

      setUsername(`${finalFirstName} ${finalLastName}`);
    } else {
      setUsername(null);
    }
  }, [user]);

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="tw-relative tw-z-[9999] max-nine:tw-p-2"
        onClose={onClose}
      >
        <div className="tw-fixed tw-top-0 tw-left-0 tw-flex tw-items-center tw-justify-center">
          <TransitionChild
            as={Fragment}
            enter="tw-ease-out tw-duration-300"
            enterFrom="tw-opacity-0 tw-scale-95"
            enterTo="tw-opacity-100 tw-scale-100"
            leave="tw-ease-in tw-duration-200"
            leaveFrom="tw-opacity-100 tw-scale-100"
            leaveTo="tw-opacity-0 tw-scale-95"
          >
            <DialogPanel
              className={clsx(
                'tw-min-h-screen tw-bg-white tw-w-screen tw-px-3 tw-py-7'
              )}
            >
              <div className="tw-h-screen tw-overflow-y-scroll tw-px-3 tw-pb-24">
                <div className="tw-sticky tw-top-0 tw-left-0 tw-flex tw-items-center tw-justify-center tw-border tw-border-solid tw-border-platinum-gray tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-pb-4 tw-py-2 tw-bg-white">
                  <DefaultButton
                    aria-label={translate(HeaderEnum['mobile_menu.close_menu'])}
                    onClick={() => onClose()}
                    className="tw-absolute tw-left-0 tw--translate-y-1/2 tw-top-5"
                  >
                    <Image
                      src={ImagesHeaderCloseSvg}
                      alt="close"
                      width={24}
                      height={24}
                    />
                  </DefaultButton>

                  <p className="tw-p-0 tw-m-0 tw-font-plusJakartaSans tw-font-semibold tw-leading-[20.16px] tw-text-base tw-text-center tw-text-textBlack">
                    {translate(HeaderEnum['mobile_menu.account'])}
                  </p>
                </div>
                <UserMenuDialogMenu
                  username={username}
                  isLoadingUser={isLoadingUser}
                  notificationsDataLength={notificationsData.length}
                  buyerNotificationsCallToAc={buyerNotificationsCallToAc}
                  sellerNotificationsCallToAc={sellerNotificationsCallToAc}
                  user={user}
                  ctaCountQuery={ctaCountQuery}
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
